/**
 * PromotionRegisterPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  InputFormTable,
  Editor,
  UploadFile,
  Text,
  ModalLayout,
} from 'components';
import {
  Select,
  Input,
  Button,
  DatePicker,
  Radio,
  Table,
  Flex,
  InputNumber,
  Space,
  Avatar,
  Tag,
  Image,
} from 'antd';
import { stringToMoneyFormat, STORE_URL } from 'utils';
import { APPLICATION_STATUS, PRODUCT_STATUS } from 'constants/ProductConstants';
import dayjs from 'dayjs';
import { SearchOutlined, MinusOutlined } from '@ant-design/icons';

const DATE_FORMAT = 'YYYY.MM.DD HH:mm';

const RATE_TYPE = [
  {
    label: '%',
    value: 'R',
  },
  {
    label: '₩',
    value: 'W',
  },
];

/**----------------------------------------
 * [Component] 총관리자: 프로모션 등록 > 프레젠터
 * ----------------------------------------
 */
const PromotionRegisterPresenter = (props) => {
  const {
    PROMOTION_INITIAL_VALUE,
    DISCOUNT_INITIAL_VALUE,
    data,
    inputValue,
    onInputValue,
    onDiscount,
    onSubmit,
    onChange,
    discount,
    setDiscount,
    /* Default inputValue */
    productList,
    categoryList,
    /* New inputValue */
    selectedList,
    onSelect,
    file = null,
    onFileChange = () => {},
    modalType,
    onOpen = () => {},
    currentPage = 1,
    setCurrentPage = () => {},
    searchText = '',
    setSearchText = () => {},
    searchedColumn = '',
    setSearchedColumn = () => {},
    isDuplicateOpened = false,
    onDuplicateOpen = () => {},
    onDuplicateProductCheck = () => {},
    duplicateProductList = [],
    onDuplicateSelect = () => {},
  } = props;
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  // 선택항목
  const searchInput = React.useRef(null);
  const [tableKey, setTableKey] = React.useState(Date.now());
  const [pageSize, setPageSize] = React.useState(10);
  // const [target, setTarget] = useState('all');

  /**
   * 테이블 검색 함수
   * @param {*} selectedKeys
   * @param {*} confirm
   * @param {*} dataIndex
   */
  const handleTableSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleTableReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText('');
    setSearchedColumn('');
    confirm();
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`검색어를 입력해주세요`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleTableSearch(selectedKeys, confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleTableSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            검색
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleTableReset(clearFilters, confirm)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            초기화
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex.replace('duplicate_', '')]
        ?.toString()
        ?.toLowerCase()
        ?.includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) => text,
  });

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    subTitle: {
      fontSize: 17,
      fontWeight: 500,
      lineHeight: '141%',
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  /**
   * SECTION: 기본 정보
   */
  const form1 = [
    {
      label: '프로모션명',
      form: [
        {
          input: (
            <Input
              size="large"
              placeholder="프로모션명 입력"
              style={{ ...styles.form.input.large, width: 800 }}
              value={inputValue.promotion_title}
              onChange={(e) => onChange('promotion_title', e.target.value)}
              count={{
                show: true,
                max: 50,
              }}
            />
          ),
        },
      ],
      required: true,
    },
    {
      label: '프로모션 설명',
      form: [
        {
          input: (
            <Input.TextArea
              size="large"
              placeholder="프로모션 설명 입력"
              style={{ ...styles.form.input.large, width: 800 }}
              value={inputValue.promotion_description}
              onChange={(e) =>
                onChange('promotion_description', e.target.value)
              }
              rows={4}
              count={{
                show: true,
                max: 250,
              }}
            />
          ),
        },
      ],
      required: true,
    },
    // {
    //   label: '프로모션 코드',
    //   form: [
    //     {
    //       input: (
    //         <Input
    //           size="large"
    //           placeholder="프로모션코드 입력"
    //           style={{ ...styles.form.input.large, width: 800 }}
    //           value={inputValue.promotion_name}
    //           onChange={(e) => onChange('promotion_code', e.target.value)}
    //           count={{
    //             show: true,
    //             max: 100,
    //           }}
    //         />
    //       ),
    //     },
    //   ],
    // },

    {
      label: '프로모션 내용',
      form: [
        {
          input: (
            <Editor
              placeholder={'프로모션 내용을 입력해주세요'}
              initialContent={inputValue?.promotion_content}
              editorOnChange={(content) =>
                onChange('promotion_content', content)
              }
              height={430}
              width="800"
            />
          ),
        },
      ],
      required: true,
    },
    {
      label: '프로모션 썸네일',
      form: [
        {
          input: (
            <Flex gap={12}>
              {inputValue?.thumbnail_url ? (
                <div style={{ position: 'relative', top: 0, left: 0 }}>
                  <Image
                    style={{ height: '130px' }}
                    src={`${STORE_URL}/${inputValue?.thumbnail_url}`}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<MinusOutlined />}
                    style={{ position: 'absolute', top: -10, right: -10 }}
                    onClick={() => onChange('thumbnail_url', null)}
                  />
                </div>
              ) : (
                <UploadFile
                  files={file ? [file] : []}
                  preview
                  onChange={(files) => onFileChange(files[0])}
                />
              )}
            </Flex>
          ),
        },
      ],
    },
    {
      label: '프로모션 기간',
      form: [
        {
          input: (
            <Flex gap={4} align="center">
              <DatePicker
                disabled={
                  modalType === 'UPDATE' &&
                  inputValue.promotion_start_date < dayjs().unix()
                }
                value={dayjs.unix(inputValue.promotion_start_date)}
                format={DATE_FORMAT}
                onChange={(e) =>
                  onChange('promotion_start_date', dayjs(e).unix())
                }
                placeholder="프로모션 시작일"
                minDate={dayjs()}
                maxDate={dayjs.unix(inputValue.promotion_end_date)}
                showTime
                disabledTime={(date) => {
                  const end = dayjs.unix(inputValue.promotion_end_date);
                  if (!date || !end.isSame(date, 'day')) return {};
                  return {
                    disabledHours: () =>
                      [...Array(24).keys()].filter((h) => h > end.hour()),
                    disabledMinutes: (h) =>
                      h === end.hour()
                        ? [...Array(60).keys()].filter((m) => m > end.minute())
                        : [],
                  };
                }}
              />
              <p>~</p>
              <DatePicker
                value={dayjs.unix(inputValue.promotion_end_date)}
                format={DATE_FORMAT}
                onChange={(e) =>
                  onChange('promotion_end_date', dayjs(e).unix())
                }
                placeholder="프로모션 종료일"
                minDate={dayjs.unix(inputValue.promotion_start_date)}
                showTime
                disabledTime={(date) => {
                  const start = dayjs.unix(inputValue.promotion_start_date);
                  if (!date || !start.isSame(date, 'day')) return {};
                  return {
                    disabledHours: () =>
                      [...Array(24).keys()].filter((h) => h < start.hour()),
                    disabledMinutes: (h) =>
                      h === start.hour()
                        ? [...Array(60).keys()].filter(
                            (m) => m < start.minute()
                          )
                        : [],
                  };
                }}
              />
            </Flex>
          ),
        },
      ],
      required: true,
    },
    {
      label: '노출여부',
      form: [
        {
          input: (
            <>
              <Radio.Group
                value={inputValue.is_show}
                onChange={(e) => onChange('is_show', e.target.value)}
              >
                <Radio value={'Y'}>노출</Radio>
                <Radio value={'N'}>숨김</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    {
      label: '메인노출여부',
      form: [
        {
          input: (
            <>
              <Radio.Group
                value={inputValue.is_main}
                onChange={(e) => onChange('is_main', e.target.value)}
              >
                <Radio value={'Y'}>메인 노출</Radio>
                <Radio value={'N'}>메인 숨김</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    {
      label: '프로모션 타입',
      form: [
        {
          input: (
            <>
              <Radio.Group
                value={inputValue.promotion_type}
                onChange={(e) => onChange('promotion_type', e.target.value)}
              >
                <Radio value={'PROMOTION'}>기간할인</Radio>
                <Radio value={'TIME_SALE'}>타임세일</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    {
      label: '심사상태',
      form: [
        {
          input: (
            <>
              <Radio.Group
                value={inputValue.examination_status}
                onChange={(e) => onChange('examination_status', e.target.value)}
              >
                <Radio value={'WAIT'}>대기</Radio>
                <Radio value={'APPLY'}>신청</Radio>
                <Radio value={'REJECT'}>반려</Radio>
                <Radio value={'APPROVE'}>승인</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
  ];

  /**
   * SECTION: 할인 설정
   * --
   */
  const form2 = [
    {
      label: '할인 설정',
      form: [
        {
          input: (
            <InputNumber
              size="large"
              placeholder="숫자만 입력"
              style={{ ...styles.form.input.medium }}
              value={discount.discount_rate}
              onChange={(e) => onChange('discount_rate', e, setDiscount)}
            />
          ),
        },
        {
          input: (
            <Select
              size="large"
              placeholder="%"
              style={{ ...styles.form.input.medium, width: 80 }}
              value={discount.discount_type}
              onChange={(e) => onChange('discount_type', e, setDiscount)}
              options={RATE_TYPE}
            />
          ),
        },
        {
          input: (
            <>
              <p>최대</p>
              <InputNumber
                size="large"
                placeholder="숫자만 입력"
                style={{ ...styles.form.input.large }}
                value={discount.max_discount_price}
                onChange={(e) => onChange('max_discount_price', e, setDiscount)}
              />
              <p>원 할인</p>
            </>
          ),
        },
      ],
      required: true,
    },
    {
      label: '적립 설정',
      form: [
        {
          input: (
            <InputNumber
              size="large"
              placeholder="숫자만 입력"
              style={{ ...styles.form.input.medium }}
              value={discount.reserve_rate}
              onChange={(e) => onChange('reserve_rate', e, setDiscount)}
            />
          ),
        },
        {
          input: (
            <Select
              size="large"
              placeholder="%"
              style={{ ...styles.form.input.medium, width: 80 }}
              value={discount.reserve_type}
              onChange={(e) => onChange('reserve_type', e, setDiscount)}
              options={RATE_TYPE}
            />
          ),
        },
      ],
      required: true,
    },
    {
      label: '수수료 설정',
      form: [
        {
          input: (
            <InputNumber
              size="large"
              placeholder="숫자만 입력"
              style={{ ...styles.form.input.medium }}
              value={discount.commission_rate}
              onChange={(e) => onChange('commission_rate', e, setDiscount)}
            />
          ),
        },
        {
          input: (
            <Select
              size="large"
              placeholder="%"
              style={{ ...styles.form.input.medium, width: 80 }}
              value={discount.commission_type}
              onChange={(e) => onChange('commission_type', e, setDiscount)}
              options={RATE_TYPE}
            />
          ),
        },
      ],
      required: true,
    },
    {
      label: '분담 비율 설정',
      form: [
        {
          input: (
            <InputNumber
              size="large"
              placeholder="숫자만 입력"
              style={{ ...styles.form.input.medium }}
              value={discount.share_ratio}
              onChange={(e) => onChange('share_ratio', e, setDiscount)}
              suffix={'%'}
            />
          ),
        },
      ],
      required: true,
    },
    {
      label: '활성화여부',
      form: [
        {
          input: (
            <>
              <Radio.Group
                value={discount?.is_show}
                onChange={(e) =>
                  onChange('is_show', e.target.value, setDiscount)
                }
              >
                <Radio value={'Y'}>활성화</Radio>
                <Radio value={'N'}>비활성화</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
  ];

  // /**
  //  * SECTION: 할인 대상
  //  * --
  //  */
  // const form3 = [
  //   {
  //     label: '할인 대상',
  //     form: [
  //       {
  //         input: (
  //           <>
  //             <Radio.Group
  //               value={inputValue.target}
  //               onChange={(e) => onChange('target', e.target.value)}
  //             >
  //               <Radio value={'all'}>전체상품</Radio>
  //               <Radio value={'brand'}>브랜드</Radio>
  //               <Radio value={'category'}>카테고리</Radio>
  //               <Radio value={'product'}>개별상품</Radio>
  //             </Radio.Group>
  //           </>
  //         ),
  //       },
  //     ],
  //     required: true,
  //   },
  // ];

  /**
   * SECTION 상품 목록 테이블 컬럼
   * --
   */
  const columns = React.useMemo(() => {
    return {
      PRODUCT_LIST: [
        {
          key: 'product_id',
          dataIndex: 'product_id',
          title: 'No',
          sorter: (a, b) => a.product_id - b.product_id,
        },
        {
          key: 'shop_information_name',
          dataIndex: 'shop_information_name',
          title: '매장명',
          ...getColumnSearchProps('shop_information_name'),
          sorter: (a, b) =>
            a.shop_information_name.localeCompare(
              b.shop_information_name,
              'ko'
            ),
          render: (shop_information_name) => shop_information_name,
        },
        {
          key: 'product_name',
          dataIndex: 'product_name',
          title: '상품명',
          ...getColumnSearchProps('product_name'),
          sorter: (a, b) => a.product_name.localeCompare(b.product_name, 'ko'),
          render: (item, { thumbnail }) => (
            <Flex gap={8} align="center">
              <Avatar src={thumbnail} shape="square" size={64} />
              {item}
            </Flex>
          ),
        },
        {
          key: 'product_code',
          title: '상품코드',
          ...getColumnSearchProps('product_code'),
          dataIndex: 'product_code',
        },
        {
          key: 'product_category_name',
          dataIndex: 'product_category_name',
          title: '카테고리',
          sorter: (a, b) =>
            a.product_category_name.localeCompare(
              b.product_category_name,
              'ko'
            ),
          render: (product_category_name) => product_category_name,
          filters: categoryList.map((_item) => ({
            text: _item.product_category_name,
            value: _item.product_category_id,
          })),
          onFilter: (value, record) => record.product_category_id === value,
        },
        {
          key: 'price',
          dataIndex: 'price',
          title: '단가',
          render: (price) => stringToMoneyFormat(price, '원'),
          sorter: (a, b) => a.price - b.price,
        },
        {
          key: 'is_show',
          dataIndex: 'is_show',
          title: '노출여부',
          render: (item) => (
            <Tag color={item === 'Y' ? 'processing' : 'default'}>
              {item === 'Y' ? '노출' : '숨김'}
            </Tag>
          ),
          filters: [
            {
              text: '노출',
              value: 'Y',
            },
            {
              text: '숨김',
              value: 'N',
            },
          ],
          onFilter: (value, record) => record.is_show === value,
        },
        {
          key: 'product_status',
          dataIndex: 'product_status',
          title: '상품판매상태',
          render: (item) => (
            <Tag color={PRODUCT_STATUS[item]?.color}>
              {PRODUCT_STATUS[item]?.title}
            </Tag>
          ),
          filters: Object.values(PRODUCT_STATUS).map((item) => ({
            text: item.title,
            value: item.value,
          })),
          onFilter: (value, record) => record.product_status === value,
        },
        {
          key: 'application_status',
          dataIndex: 'application_status',
          title: '상품심사상태',
          render: (item) => (
            <Tag color={APPLICATION_STATUS[item]?.color}>
              {APPLICATION_STATUS[item]?.title}
            </Tag>
          ),
          filters: Object.entries(APPLICATION_STATUS).map(([key, value]) => ({
            text: value.title,
            value: key,
          })),
          onFilter: (value, record) => record.application_status === value,
        },
      ],
      DUPLICATE_LIST: [
        {
          key: 'product_id',
          dataIndex: 'product_id',
          title: 'No',
          sorter: (a, b) => a.product_id - b.product_id,
        },
        {
          key: 'shop_information_name',
          dataIndex: 'shop_information_name',
          title: '매장명',
          ...getColumnSearchProps('duplicate_shop_information_name'),
          sorter: (a, b) =>
            a.shop_information_name.localeCompare(
              b.shop_information_name,
              'ko'
            ),
          render: (shop_information_name) => shop_information_name,
        },
        {
          key: 'product_name',
          dataIndex: 'product_name',
          title: '상품명',
          ...getColumnSearchProps('duplicate_product_name'),
          sorter: (a, b) => a.product_name.localeCompare(b.product_name, 'ko'),
          render: (product_name, { thumbnail }) => (
            <Flex gap={8} align="center">
              <Avatar
                src={`${STORE_URL}/${thumbnail}`}
                shape="square"
                size={64}
              />
              {product_name}
            </Flex>
          ),
        },
        {
          key: 'promotion_title',
          dataIndex: 'promotion_title',
          title: '프로모션명',
          ...getColumnSearchProps('duplicate_promotion_title'),
          render: (promotion_title) => (
            <span style={{ fontWeight: 'bold' }}>{promotion_title}</span>
          ),
        },
        {
          key: 'promotion_end_date',
          title: '프로모션 종료기간',
          dataIndex: 'promotion_end_date',
          render: (item) => (
            <span style={{ fontWeight: 'bold' }}>
              {dayjs.unix(item).format('YYYY.MM.DD HH:mm:ss')}
            </span>
          ),
          sorter: (a, b) => a.promotion_end_date - b.promotion_end_date,
        },
        {
          key: 'product_category_name',
          dataIndex: 'product_category_name',
          title: '카테고리',
          sorter: (a, b) =>
            a.product_category_name.localeCompare(
              b.product_category_name,
              'ko'
            ),
          render: (product_category_name) => product_category_name,
          filters: categoryList.map((_item) => ({
            text: _item.product_category_name,
            value: _item.product_category_id,
          })),
          onFilter: (value, record) => record.product_category_id === value,
        },
        {
          key: 'price',
          dataIndex: 'price',
          title: '단가',
          render: (price) => stringToMoneyFormat(price, '원'),
          sorter: (a, b) => a.price - b.price,
        },
        {
          key: 'is_show',
          dataIndex: 'is_show',
          title: '노출여부',
          render: (item) => (
            <Tag color={item === 'Y' ? 'processing' : 'default'}>
              {item === 'Y' ? '노출' : '숨김'}
            </Tag>
          ),
          filters: [
            {
              text: '노출',
              value: 'Y',
            },
            {
              text: '숨김',
              value: 'N',
            },
          ],
          onFilter: (value, record) => record.is_show === value,
        },
        {
          key: 'product_status',
          dataIndex: 'product_status',
          title: '상품판매상태',
          render: (item) => (
            <Tag color={PRODUCT_STATUS[item]?.color}>
              {PRODUCT_STATUS[item]?.title}
            </Tag>
          ),
          filters: Object.values(PRODUCT_STATUS).map((item) => ({
            text: item.title,
            value: item.value,
          })),
          onFilter: (value, record) => record.product_status === value,
        },
        {
          key: 'application_status',
          dataIndex: 'application_status',
          title: '상품심사상태',
          render: (item) => (
            <Tag color={APPLICATION_STATUS[item]?.color}>
              {APPLICATION_STATUS[item]?.title}
            </Tag>
          ),
          filters: Object.entries(APPLICATION_STATUS).map(([key, value]) => ({
            text: value.title,
            value: key,
          })),
          onFilter: (value, record) => record.application_status === value,
        },
      ],
    };
  }, [categoryList]);
  /**
   * 테이블 초기화 함수
   * --
   */
  const handleResetFilters = () => {
    setSearchText('');
    setSearchedColumn('');
    setTableKey(Date.now()); // 새로운 키 부여 → Table 전체 리렌더
    if (searchInput.current) {
      searchInput.current.input.value = '';
    }
  };

  /* ===== FUNCTION ===== */

  /**
   * 페이지 네이션 함수
   * @param {number} page
   * --
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  /* ===== HOOKS ===== */
  React.useEffect(() => handleResetFilters(), [data]);

  const newProduct = React.useMemo(() => {
    let arr = productList;
    const searchTitle = searchedColumn.split('_')?.[0];
    if (searchText && searchedColumn && searchTitle !== 'duplicate') {
      arr = arr.filter((item) => {
        return item[searchedColumn]?.toString()?.includes(searchText);
      });
    }
    arr = [
      ...arr.filter((item) =>
        selectedList.some((select) => select.product_id === item.product_id)
      ),
      ...arr.filter(
        (item) =>
          !selectedList.some((select) => select.product_id === item.product_id)
      ),
    ];
    return arr;
  }, [searchText, searchedColumn, productList, selectedList]);

  /* ===== RENDER ===== */
  /**
   * 테이블 초기화 동작
   * --
   */
  // React.useEffect(() => handleResetFilters(), [data]);

  return (
    <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
      <Row>
        <Col x={24}>
          <Title style={{ ...styles.title }}>프로모션 등록</Title>

          {/* 기본정보 */}
          <Content
            maxWidth={'100%'}
            padding={20}
            borderRadius={4}
            style={{ marginBottom: 40 }}
          >
            <Title style={{ ...styles.subTitle }}>기본 정보</Title>
            <InputFormTable form={form1} />
          </Content>
          {/* 기본정보 끝 */}

          {/* 혜택 설정 */}
          <Content
            maxWidth={'100%'}
            padding={20}
            borderRadius={4}
            style={{ marginBottom: 40 }}
          >
            <Title style={{ ...styles.subTitle }}>혜택 설정</Title>
            <InputFormTable form={form2} />
          </Content>
          {/* 혜택 설정 */}

          {/* 대상 설정 */}
          <Content maxWidth={'100%'} padding={20} borderRadius={4}>
            {/* <Title style={{ ...styles.subTitle }}>대상설정</Title>
            <InputFormTable form={form3} /> */}

            {/* 적용상품 테이블 */}
            <div style={{ marginTop: 14 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 20,
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Flex gap={8}>
                  <Title style={{ ...styles.subTitle }}>상품 선택</Title>
                  <Text size={14} style={{ ...styles.subTitle }}>
                    <span style={{ color: '#0066FF' }}>
                      {selectedList?.length}
                    </span>
                    개의 상품 선택 됨
                  </Text>
                </Flex>
                <Flex gap={8}>
                  <Button onClick={() => onSelect(newProduct)}>
                    검색된 항목 전체 선택
                  </Button>
                  <Button onClick={() => onSelect([])}>
                    검색된 항목 전체 선택해제
                  </Button>
                </Flex>
              </div>
              {/* === 상품 list: start === */}
              <div>
                <Table
                  key={tableKey}
                  rowKey={'key'}
                  size="small"
                  dataSource={newProduct}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showSizeChanger: true,
                    onChange: handlePageChange,
                    onShowSizeChange: (size) => setPageSize(size),
                    total: newProduct?.length || 0,
                  }}
                  rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: selectedList?.map((item) => item.key),
                    onChange: (_selectedRowKeys, _selectedRows) => {
                      onSelect(_selectedRows);
                    },
                  }}
                  scroll={{ x: 'max-content' }}
                  columns={columns['PRODUCT_LIST']}
                />
              </div>
              {/* === 상품 list: end === */}
              {/* === 브랜드 list: start === */}
              {/* {inputValue.target === 'brand' && (
                <div>
                  <Table
                    size="small"
                    dataSource={shopInformationList.map((item) => ({
                      ...item,
                      key: item.shop_information_id,
                    }))}
                    rowSelection={{
                      type: 'checkbox',
                      selectedRowKeys: selectedList?.map((item) => item.key),
                      onChange: (_selectedRowKeys, _selectedRows) => {
                        console.log('_selectedRows: ', _selectedRows);

                        setSelectedList(_selectedRows);
                      },
                    }}
                    columns={[
                      {
                        title: 'No',
                        dataIndex: 'key',
                      },
                      {
                        title: '상품명',
                        dataIndex: 'shop_information_name',
                      },
                      {
                        title: '주소지',
                        dataIndex: 'shop_information_address',
                      },
                      {
                        title: '입점유형',
                        dataIndex: 'type',
                      },
                    ]}
                  />
                </div>
              )} */}
              {/* === 브랜드 list: end === */}
              {/* === 카테고리 list: start === */}

              {/* {inputValue.target === 'category' && (
                <div>
                  <Table
                    size="small"
                    dataSource={categoryList.map((item) => ({
                      ...item,
                      key: item.product_category_id,
                    }))}
                    rowSelection={{
                      type: 'checkbox',
                      selectedRowKeys: selectedList?.map((item) => item.key),
                      onChange: (_selectedRowKeys, _selectedRows) => {
                        setSelectedList(_selectedRows);
                      },
                    }}
                    columns={[
                      {
                        title: 'No',
                        dataIndex: 'key',
                      },
                      {
                        title: '카테고리명',
                        dataIndex: 'product_category_name',
                      },
                      {
                        title: '메뉴유형',
                        dataIndex: 'type',
                      },
                    ]}
                  />
                </div>
              )} */}
              {/* === 카테고리 list: end === */}
              {/* === 상품 list: start === */}
              {/* {inputValue.target === 'product' && (
                <div>
                  <Table
                    size="small"
                    dataSource={productList.map((item) => ({
                      ...item,
                      key: item.product_id,
                    }))}
                    rowSelection={{
                      type: 'checkbox',
                      selectedRowKeys: selectedList?.map((item) => item.key),
                      onChange: (_selectedRowKeys, _selectedRows) => {
                        setSelectedList(_selectedRows);
                      },
                    }}
                    columns={columns}
                  />
                </div>
               )} */}
              {/* === 상품 list: end === */}
            </div>

            {/* 적용상품 테이블 끝 */}
          </Content>
          {/* 대상 설정 끝 */}

          {/* 저장/취소 버튼 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              marginTop: 20,
            }}
          >
            <Button
              size="large"
              type="primary"
              style={{ ...styles.button, background: 'var(--primary-color)' }}
              onClick={() => onDuplicateProductCheck()}
            >
              {modalType === 'CREATE' ? '저장' : '수정'}
            </Button>
            <Button
              size="large"
              style={{ ...styles.button }}
              onClick={() => {
                onDuplicateSelect([]);
                onDuplicateOpen(false);
                onFileChange('');
                onInputValue(PROMOTION_INITIAL_VALUE);
                onDiscount(DISCOUNT_INITIAL_VALUE);
                onOpen(false);
              }}
            >
              취소
            </Button>
          </div>
          {/* 저장/취소 버튼 끝 */}
          {/* 중복 상품 모달 */}
          <ModalLayout
            type="modal"
            centered
            open={isDuplicateOpened}
            onCancel={() => {
              onDuplicateSelect([]);
              onDuplicateOpen(false);
            }}
            closable
            width={'80%'}
            style={{ maxWidth: '80%' }}
            height={'80%'}
            // footer={

            // }
          >
            <Content
              maxWidth={'100%'}
              padding={0}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Flex
                vertical
                gap={24}
                style={{ padding: '12px 0px', width: '100%' }}
              >
                <Title>
                  <span>
                    <span style={{ color: 'red' }}>중복된 상품</span> 총{' '}
                    <span style={{ color: '#0066FF' }}>
                      {duplicateProductList?.length}
                    </span>
                    개 (*저장시 제거 후 등록)
                  </span>
                </Title>
                <Table
                  rowKey={'key'}
                  columns={columns['DUPLICATE_LIST']}
                  dataSource={duplicateProductList}
                  scroll={{
                    x: 'max-content',
                  }}
                  // rowSelection={{
                  //   onChange: (_, rows) => onSelect(rows),
                  // }}
                  // onRow={(record) => ({
                  //   onClick: () => {
                  //     setModalType('UPDATE');
                  //     onOpen(true);
                  //     setModalData({ ...record, key: dayjs().unix() });
                  //   },
                  // })}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 20,
                  }}
                >
                  <Button
                    size="large"
                    type="primary"
                    style={{
                      ...styles.button,
                      background: 'var(--primary-color)',
                    }}
                    onClick={() => {
                      modalType === 'CREATE'
                        ? onSubmit('CREATE')
                        : onSubmit('UPDATE');
                    }}
                  >
                    {modalType === 'CREATE' ? '저장' : '수정'}
                  </Button>
                  <Button
                    size="large"
                    style={{ ...styles.button }}
                    onClick={() => {
                      onDuplicateOpen(false);
                      onDuplicateSelect([]);
                    }}
                  >
                    취소
                  </Button>
                </div>
              </Flex>
            </Content>
          </ModalLayout>
        </Col>
      </Row>
    </Content>
  );
};

export default PromotionRegisterPresenter;
